import React, { useEffect, useState } from 'react';
import { Notification, NotificationType, notificationService } from '../services/NotificationService';
import { ResponsiveButton } from './ResponsiveButton';
import './NotificationContainer.css';

interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onDismiss }) => {
  const getNotificationIcon = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.SUCCESS:
        return '✅';
      case NotificationType.ERROR:
        return '❌';
      case NotificationType.WARNING:
        return '⚠️';
      case NotificationType.INFO:
        return 'ℹ️';
      case NotificationType.TURN_BASED:
        return '🎮';
      case NotificationType.GAME_INVITE:
        return '🎯';
      case NotificationType.GAME_RESULT:
        return '🏆';
      default:
        return 'ℹ️';
    }
  };

  const getNotificationClassName = (type: NotificationType): string => {
    const baseClass = 'notification-item';
    const typeClass = `notification-item--${type.replace('_', '-')}`;
    return `${baseClass} ${typeClass}`;
  };

  return (
    <div className={getNotificationClassName(notification.type)}>
      <div className="notification-item__icon">
        {getNotificationIcon(notification.type)}
      </div>
      
      <div className="notification-item__content">
        <div className="notification-item__title">
          {notification.title}
        </div>
        <div className="notification-item__message">
          {notification.message}
        </div>
        
        {notification.actionLabel && notification.onAction && (
          <div className="notification-item__actions">
            <ResponsiveButton
              variant="outline"
              size="small"
              onClick={notification.onAction}
            >
              {notification.actionLabel}
            </ResponsiveButton>
          </div>
        )}
      </div>
      
      <button
        className="notification-item__dismiss"
        onClick={() => onDismiss(notification.id)}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
};

interface NotificationContainerProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxNotifications?: number;
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  position = 'top-right',
  maxNotifications = 5
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // In production, suppress non-critical notifications (INFO, SUCCESS) to reduce popup noise
  const isProduction = process.env.REACT_APP_ENV === 'production';
  const suppressInfoNotifications = isProduction && process.env.REACT_APP_SUPPRESS_NOTIFICATIONS !== 'false';

  useEffect(() => {
    const unsubscribe = notificationService.subscribe((newNotifications) => {
      let filtered = newNotifications;
      if (suppressInfoNotifications) {
        filtered = newNotifications.filter(n =>
          n.type === NotificationType.ERROR ||
          n.type === NotificationType.WARNING ||
          n.type === NotificationType.GAME_INVITE ||
          n.type === NotificationType.TURN_BASED ||
          n.persistent
        );
      }
      setNotifications(filtered.slice(0, maxNotifications));
    });

    // Load initial notifications
    let initial = notificationService.getNotifications();
    if (suppressInfoNotifications) {
      initial = initial.filter(n =>
        n.type === NotificationType.ERROR ||
        n.type === NotificationType.WARNING ||
        n.type === NotificationType.GAME_INVITE ||
        n.type === NotificationType.TURN_BASED ||
        n.persistent
      );
    }
    setNotifications(initial.slice(0, maxNotifications));

    return unsubscribe;
  }, [maxNotifications, suppressInfoNotifications]);

  const handleDismiss = (id: string) => {
    notificationService.removeNotification(id);
  };

  const handleClearAll = () => {
    notificationService.clearAll();
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className={`notification-container notification-container--${position}`}>
      <div className="notification-container__header">
        {notifications.length > 1 && (
          <button
            className="notification-container__clear-all"
            onClick={handleClearAll}
            aria-label="Clear all notifications"
          >
            Clear All
          </button>
        )}
      </div>
      
      <div className="notification-container__list">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onDismiss={handleDismiss}
          />
        ))}
      </div>
    </div>
  );
};

export default NotificationContainer;